"use client";

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Box } from '@/components/atoms/Box';
import { Text } from '@/components/atoms/Text';
import { RawButton } from '@/components/atoms/RawButton';
import type { PaginationControlsProps } from './PaginationControls.props';

/**
 * Determines which page numbers to display, including ellipsis markers.
 * Returns an array of page numbers or 'ellipsis' placeholders.
 */
function getVisiblePages(currentPage: number, totalPages: number): ReadonlyArray<number | 'ellipsis'> {
  const pages: Array<number | 'ellipsis'> = [];
  for (let page = 1; page <= totalPages; page++) {
    const isFirstOrLast = page === 1 || page === totalPages;
    const isNearCurrent = Math.abs(page - currentPage) <= 1;
    if (isFirstOrLast || isNearCurrent) {
      pages.push(page);
    } else if (page === currentPage - 2 && currentPage > 3) {
      pages.push('ellipsis');
    } else if (page === currentPage + 2 && currentPage < totalPages - 2) {
      pages.push('ellipsis');
    }
  }
  return pages;
}

/**
 * PaginationControls molecule.
 *
 * Renders page navigation with previous/next chevron buttons and
 * numbered page buttons. Automatically inserts ellipsis for large
 * page counts.
 */
export function PaginationControls(t0) {
  const $ = _c(45);
  const {
    currentPage,
    totalPages,
    onPageChange,
    className
  } = t0;
  if (totalPages <= 1) {
    return null;
  }
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== className || $[1] !== currentPage || $[2] !== onPageChange || $[3] !== totalPages) {
    const visiblePages = getVisiblePages(currentPage, totalPages);
    T1 = Box;
    if ($[10] !== className) {
      t3 = cn("flex items-center justify-center gap-2", className);
      $[10] = className;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    let t5;
    if ($[12] !== currentPage || $[13] !== onPageChange) {
      t5 = () => onPageChange(currentPage - 1);
      $[12] = currentPage;
      $[13] = onPageChange;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    const t6 = currentPage === 1;
    const t7 = currentPage === 1 ? "border-border/50 text-muted-foreground/40 cursor-not-allowed" : "border-border text-muted-foreground hover:bg-accent hover:border-border";
    let t8;
    if ($[15] !== t7) {
      t8 = cn("flex items-center justify-center w-10 h-10 rounded-lg border transition-colors", t7);
      $[15] = t7;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    let t9;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <ChevronLeft className="w-5 h-5" />;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    if ($[18] !== t5 || $[19] !== t6 || $[20] !== t8) {
      t4 = <RawButton onClick={t5} disabled={t6} className={t8} aria-label="Previous page">{t9}</RawButton>;
      $[18] = t5;
      $[19] = t6;
      $[20] = t8;
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    T0 = Box;
    t1 = "flex items-center gap-1";
    let t10;
    if ($[22] !== currentPage || $[23] !== onPageChange) {
      t10 = (item, index) => {
        if (item === "ellipsis") {
          return <Text key={`ellipsis-${index}`} className="px-2 text-muted-foreground/60">...</Text>;
        }
        const page = item;
        return <RawButton key={page} onClick={() => onPageChange(page)} className={cn("w-10 h-10 rounded-lg border font-medium transition-colors", page === currentPage ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:bg-accent hover:border-border")} aria-label={`Page ${page}`} aria-current={page === currentPage ? "page" : undefined}>{page}</RawButton>;
      };
      $[22] = currentPage;
      $[23] = onPageChange;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    t2 = visiblePages.map(t10);
    $[0] = className;
    $[1] = currentPage;
    $[2] = onPageChange;
    $[3] = totalPages;
    $[4] = T0;
    $[5] = T1;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    T0 = $[4];
    T1 = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  let t5;
  if ($[25] !== T0 || $[26] !== t1 || $[27] !== t2) {
    t5 = <T0 className={t1}>{t2}</T0>;
    $[25] = T0;
    $[26] = t1;
    $[27] = t2;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== currentPage || $[30] !== onPageChange) {
    t6 = () => onPageChange(currentPage + 1);
    $[29] = currentPage;
    $[30] = onPageChange;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  const t7 = currentPage === totalPages;
  const t8 = currentPage === totalPages ? "border-border/50 text-muted-foreground/40 cursor-not-allowed" : "border-border text-muted-foreground hover:bg-accent hover:border-border";
  let t9;
  if ($[32] !== t8) {
    t9 = cn("flex items-center justify-center w-10 h-10 rounded-lg border transition-colors", t8);
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ChevronRight className="w-5 h-5" />;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t6 || $[36] !== t7 || $[37] !== t9) {
    t11 = <RawButton onClick={t6} disabled={t7} className={t9} aria-label="Next page">{t10}</RawButton>;
    $[35] = t6;
    $[36] = t7;
    $[37] = t9;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== T1 || $[40] !== t11 || $[41] !== t3 || $[42] !== t4 || $[43] !== t5) {
    t12 = <T1 className={t3}>{t4}{t5}{t11}</T1>;
    $[39] = T1;
    $[40] = t11;
    $[41] = t3;
    $[42] = t4;
    $[43] = t5;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  return t12;
}